import type { ClientErrorReport } from '@pixelworld/shared';

/** A broken page can throw the same error every frame; a handful is enough to see it. */
const MAX_REPORTS_PER_PAGE = 5;

/** Turns whatever was thrown into a short report, or `null` when there is nothing to tell. */
export function toErrorReport(reason: unknown, page: string): ClientErrorReport | null {
  const error = reason instanceof Error ? reason : null;
  const message = (error?.message ?? (typeof reason === 'string' ? reason : '')).trim();
  if (!message) return null;
  return {
    message: message.slice(0, 300),
    detail: error?.stack?.slice(0, 2000),
    page: page.slice(0, 200),
  };
}

/**
 * Sends errors in the browser to the server, so the admin sees them in the overview (M11).
 * Each different message once, and at most a few per page load.
 */
export function createErrorReporter(send: (report: ClientErrorReport) => void) {
  const sent = new Set<string>();
  return (reason: unknown, page: string) => {
    const report = toErrorReport(reason, page);
    if (!report || sent.has(report.message) || sent.size >= MAX_REPORTS_PER_PAGE) return;
    sent.add(report.message);
    send(report);
  };
}

export function installErrorReporting(): void {
  const report = createErrorReporter((body) => {
    void fetch('/api/v1/client-errors', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => undefined);
  });
  window.addEventListener('error', (event) =>
    report(event.error ?? event.message, location.pathname),
  );
  window.addEventListener('unhandledrejection', (event) => report(event.reason, location.pathname));
}
